import { useCallback, useEffect, useMemo, useRef } from 'react'

import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { useTransport } from '@connectrpc/connect-query'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { BiddingSpotDesiredPriceQuerySchema } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import {
  BiddingSpotService,
  PreviewBiddingSpotBidFeeAdjustedPricesRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { biddingQueryKeys } from '~/hooks/bidding/queryKeys'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useCompanyInfo } from '~/hooks/useCompanyInfo'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSpotFeeYenPerKwh } from '~/pages/SpotBidding/hooks/useSpotFeeYenPerKwh'
import {
  type BidCsvPriceQuery,
  bidCsvDesiredPriceRequests,
} from '~/pages/SpotBidding/models/bidCsvCells'
import {
  type BidCsvExportInputs,
  type BidCsvPriceOutcome,
  resolveBidCsvExportGate,
  resolveBidCsvPriceSource,
} from '~/pages/SpotBidding/models/bidCsvExportGate'

export type BidCsvExportRequest = {
  /** 価格列の逆算に問い合わせる札 (`normalBidCsvPriceQueries` / `blockBidCsvPriceQueries`)。 */
  priceQueries: readonly BidCsvPriceQuery[]
  run: (inputs: BidCsvExportInputs) => void
}

export type BidCsvExportContext = {
  /**
   * 出せない理由の文 (押せない理由として画面上に文で出す。 `conventions.md`「操作できない時の
   * ボタンの扱い」)。 押せる間 (手数料自動考慮の設定・会社情報の取得を待つ間を含む) は undefined。
   */
  blockedReason: string | undefined
  /**
   * 出力を頼む (項目を押した時に呼ぶ)。 押せる状態なら価格列をプレビュー RPC に問い合わせ、
   * 応答で出せる時だけ `run` に出力の前提 (価格列の出所と自社の事業者 ID) を渡して呼ぶ。 手数料自動考慮の
   * 設定か会社情報を取得している間は、 届くのを待ってから問い合わせる。 届いた結果押せない状態になったら
   * 呼ばない (理由は `blockedReason` に出る)。 応答で出せない時は呼ばずにエラートーストを出す。 待っている
   * 間に重ねて頼まれても、 最初の 1 回しか呼ばない (二重押しで出力が二重にならないようにする)。
   */
  requestExport: (request: BidCsvExportRequest) => void
}

/**
 * 入札一覧の CSV ダウンロードが出力の前提にする状態
 * (手数料自動考慮の設定・手数料単価・自社の事業者 ID) を集め、 押された出力を前提がそろうまで
 * 待たせる。 押せるかの判定は [[resolveBidCsvExportGate]]。 価格列の値は、 押した後にプレビュー RPC の
 * `desired_price_queries` で backend に問い合わせる (逆算の式は backend が持つ)。 一覧の cache 時点ではなく
 * 押した時点の単価で逆算するため、 react-query の cache を使わず毎回呼ぶ (`useSubmissionDuplicateCheck` と
 * 同じ形)。
 *
 * 手数料自動考慮は「スポット入札 制限 (通常・ブロック共通)」の 1 つの設定なので、 ブロック入札
 * タブも通常入札の segment (`SPOT_NORMAL`) を読む (`useBlockBidLimits` と同じ)。
 * 3 つの query は他の入札 UI と cache を共有するので、 画面から呼んでも取得は増えない。
 *
 * 手数料単価は `useSpotFeeYenPerKwh` から読む (取得の状態と JEPX 会員 ID の登録有無を持つため)。
 *
 * 応答で出せない時 (単価を使えない・値の欠け・RPC 失敗・応答どうしの食い違い) は、 押せない理由と同じ文を
 * エラートーストで出し、 入札制限と会員情報を取り直す。 押した後に初めて分かる食い違いは一過性で、
 * 常設の理由文は取り直した後の判定が出す。
 */
export function useBidCsvExportContext(): BidCsvExportContext {
  const { t } = useTranslation()
  const bidLimits = useBidLimits(BidSegment.SPOT_NORMAL)
  const fee = useSpotFeeYenPerKwh()
  const companyInfo = useCompanyInfo()

  const retailerId = companyInfo.registered ? companyInfo.retailerId : undefined
  const gate = useMemo(
    () =>
      resolveBidCsvExportGate({
        bidLimitsLoading: bidLimits.isLoading,
        bidLimitsError: bidLimits.isError,
        feeAutoConsiderEnabled: bidLimits.feeAutoConsiderEnabled,
        feeStatus: fee.status,
        feeYenPerKwh: fee.feeYenPerKwh,
        memberRegistered: fee.memberRegistered,
        companyInfoLoading: companyInfo.isLoading,
        companyInfoError: companyInfo.isError,
        retailerId,
      }),
    [
      bidLimits.isLoading,
      bidLimits.isError,
      bidLimits.feeAutoConsiderEnabled,
      fee.status,
      fee.feeYenPerKwh,
      fee.memberRegistered,
      companyInfo.isLoading,
      companyInfo.isError,
      retailerId,
    ],
  )

  const transport = useTransport()
  const queryClient = useQueryClient()
  const showErrorToast = useErrorToast()

  // 押されて gate を待っている出力。 state ではなく ref に置き、 押した直後の 2 回目も同じ render の
  // うちに弾く。
  const pendingRequestRef = useRef<BidCsvExportRequest | undefined>(undefined)
  const inFlightRef = useRef(false)
  // 問い合わせの番号。 取り消し・unmount で進め、 古い応答を捨てる。
  const sequenceRef = useRef(0)

  const queryPriceThenRun = useCallback(
    (request: BidCsvExportRequest, retailerId: string | undefined): void => {
      const sequence = ++sequenceRef.current
      inFlightRef.current = true
      const split = bidCsvDesiredPriceRequests(request.priceQueries)
      // 指値の札が無くても 1 回は呼び、 押した時点の ON/OFF を応答で確かめる。
      const chunks = split.length === 0 ? [[]] : split
      const client = createClient(BiddingSpotService, transport)
      const settle = (outcome: BidCsvPriceOutcome): void => {
        if (sequenceRef.current !== sequence) return
        inFlightRef.current = false
        if (outcome.kind === 'price') {
          request.run({ price: outcome.price, retailerId })
          return
        }
        showErrorToast({
          title: t(`page:biddingSpot.csvExport.blocked.${outcome.reason}`),
        })
        void Promise.all(
          [
            biddingQueryKeys.bidLimitMatrix,
            biddingQueryKeys.jepxMemberInfo,
          ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        )
      }
      void Promise.all(
        chunks.map((chunk) =>
          client.previewBiddingSpotBidFeeAdjustedPrices(
            create(PreviewBiddingSpotBidFeeAdjustedPricesRequestSchema, {
              desiredPriceQueries: chunk.map((q) =>
                create(BiddingSpotDesiredPriceQuerySchema, {
                  direction: q.direction,
                  orderPriceYenPerKwh: create(DecimalSchema, {
                    value: q.orderPriceYenPerKwh,
                  }),
                }),
              ),
            }),
          ),
        ),
      ).then(
        (responses) => {
          settle(resolveBidCsvPriceSource(chunks, responses))
        },
        () => {
          settle({ kind: 'failed', reason: 'feeAutoConsiderUnavailable' })
        },
      )
    },
    [queryClient, showErrorToast, t, transport],
  )

  const requestExport = useCallback(
    (request: BidCsvExportRequest): void => {
      if (pendingRequestRef.current !== undefined || inFlightRef.current) {
        return
      }
      if (gate.kind === 'ready') {
        queryPriceThenRun(request, gate.retailerId)
        return
      }
      if (gate.kind === 'blocked') return
      pendingRequestRef.current = request
    },
    [gate, queryPriceThenRun],
  )

  // gate を待っている出力は、 取得が届いて判定が `waiting` でなくなった時に 1 回だけ問い合わせるか捨てる。
  useEffect(() => {
    const request = pendingRequestRef.current
    if (request === undefined || gate.kind === 'waiting') return
    pendingRequestRef.current = undefined
    if (gate.kind === 'ready') {
      queryPriceThenRun(request, gate.retailerId)
    }
  }, [gate, queryPriceThenRun])

  useEffect(
    () => () => {
      sequenceRef.current += 1
    },
    [],
  )

  return {
    blockedReason:
      gate.kind === 'blocked'
        ? t(`page:biddingSpot.csvExport.blocked.${gate.reason}`)
        : undefined,
    requestExport,
  }
}
